import { useState } from "react";
import { useForm, useWatch } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { FormAgreementInput } from "@features/FormAgreementInput/FormAgreementInput";
import type { ProjectTypeId } from "@features/Login/types";
import { Button } from "@components/Button";
import { parseHtml } from "@components/HTMLParser/utils";
import { PencilIcon } from "@components/icons";
import { FormCheckbox } from "@components/inputs/form/FormCheckbox/FormCheckbox";
import { FormInput } from "@components/inputs/form/FormInput/FormInput";
import { FormRadio } from "@components/inputs/form/FormRadio/FormRadio";
import { Modal } from "@components/Modal/Modal";
import { useApiErrorUtils } from "@hooks/useApiErrorUtils";
import { useModal } from "@hooks/useModal";
import { useModalStore, type IUseModalProps } from "@store/modal/modal";
import { useCustomerDetailsQuery } from "@services/customer/queries";
import { useSubmitProjectDataMutation } from "@services/jointBillServiceSettings/mutations";
import { useProjectDataQuery } from "@services/jointBillServiceSettings/queries";
import type { IProjectData } from "@services/jointBillServiceSettings/types";
import { isNullOrUndefined } from "@utils/common";
import { ProjectDataConfirmModal } from "./components/ProjectDataConfirmModal/ProjectDataConfirmModal";

interface IProjectDataRenewalModalProps extends IUseModalProps {
  jointBillServiceId: number;
  projectTypeId?: ProjectTypeId;
}

export const ProjectDataRenewalModal: React.FC<
  IProjectDataRenewalModalProps
> = ({ jointBillServiceId, projectTypeId, ...modalProps }) => {
  const [isCustomerDataUpdateConfirmed, setIsCustomerDataUpdateConfirmed] =
    useState(false);
  const { t } = useTranslation();
  const modal = useModal(modalProps);
  const { create } = useModalStore();
  const { data: customerData } = useCustomerDetailsQuery();
  const { data: projectData, isError } = useProjectDataQuery(
    jointBillServiceId ?? 0,
  );

  const { mutateAsync: submitProjectData, isPending } =
    useSubmitProjectDataMutation(jointBillServiceId ?? 0);

  const isOwnerOptions = [
    {
      label: t(
        `settings.house.projectDataRenewal.form.fields.payerType.true.label`,
      ),
      value: true,
    },
    {
      label: t(
        `settings.house.projectDataRenewal.form.fields.payerType.false.label`,
      ),
      value: false,
    },
  ];

  const { control, handleSubmit, setError, setValue } = useForm<IProjectData>({
    values: {
      firstName: projectData?.firstName ?? customerData?.firstName ?? "",
      lastName: projectData?.lastName ?? customerData?.lastName ?? "",
      email: projectData?.email ?? customerData?.email ?? "",
      phone: projectData?.phone ?? customerData?.phone ?? "",
      transferringContactsAccepted:
        projectData?.transferringContactsAccepted ?? false,
      marketingRulesAccepted: customerData?.hasAcceptedMarketingRules ?? false,
      isOwner: projectData?.isOwner ?? true,
      updateCustomerData: false,
    },
  });

  const { setApiErrors } = useApiErrorUtils(setError);

  const [isOwnerValue, transferringContactsAcceptedValue] = useWatch({
    name: ["isOwner", "transferringContactsAccepted"],
    control,
  });

  const submitRenewalForm = async (data: IProjectData) => {
    if (!data.updateCustomerData && !isCustomerDataUpdateConfirmed) {
      create((modalProps) => (
        <ProjectDataConfirmModal
          {...modalProps}
          asDrawer
          onCancel={() => {
            setValue("updateCustomerData", false);
            setIsCustomerDataUpdateConfirmed(true);
          }}
          onSubmit={() => {
            setValue("updateCustomerData", true);
            setIsCustomerDataUpdateConfirmed(true);
          }}
        />
      ));
    } else {
      await submitProjectData(data).then(modal.close).catch(setApiErrors);
    }
  };

  return (
    <Modal
      modal={modal}
      className="md:max-w-xl"
      badgeProps={{ icon: PencilIcon }}
      title={t("settings.house.projectDataRenewal.modal.title")}
      actionButtons={
        <>
          {!modalProps.hideCloseButton && (
            <Button
              variant="secondary"
              className="w-full"
              onClick={modal.close}
              aria-label={t(
                "settings.house.projectDataRenewal.modal.buttons.cancel",
              )}
            >
              {t("settings.house.projectDataRenewal.modal.buttons.cancel")}
            </Button>
          )}
          <Button
            className="w-full"
            onClick={handleSubmit(submitRenewalForm)}
            disabled={!transferringContactsAcceptedValue && isOwnerValue}
            isLoading={isPending}
            aria-label={t(
              "settings.house.projectDataRenewal.modal.buttons.submit",
            )}
          >
            {t("settings.house.projectDataRenewal.modal.buttons.submit")}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        <p className="text-sm text-grey-dark">
          {t("settings.house.projectDataRenewal.modal.subtitle")}
        </p>
        <p className="text-sm text-grey-dark">
          {parseHtml(
            t(
              `settings.house.projectDataRenewal.modal.${projectData?.projectTypeId ?? projectTypeId}.description`,
              {
                link: t(
                  `externalProject.${projectData?.projectTypeId ?? projectTypeId}.rulesLink`,
                ),
              },
            ),
          )}
        </p>
        <p className="text-sm text-grey-dark">
          {parseHtml(
            t("settings.house.projectDataRenewal.modal.checkboxDescription", {
              projectName: t(
                `externalProject.${projectData?.projectTypeId ?? projectTypeId}.title`,
              ),
              address: projectData?.address,
            }),
          )}
        </p>
        <form className="flex flex-col gap-4">
          {projectData && (
            <FormRadio
              name="isOwner"
              options={isOwnerOptions}
              defaultValue={projectData.isOwner}
              control={control}
            />
          )}
          {isError && !projectData && (
            <FormRadio
              name="isOwner"
              options={isOwnerOptions}
              defaultValue={true}
              control={control}
            />
          )}
          <div className="flex flex-col justify-between gap-4 sm:flex-row">
            <FormInput
              name="firstName"
              label={t(
                "settings.house.projectDataRenewal.form.fields.firstName.label",
              )}
              control={control}
            />
            <FormInput
              name="lastName"
              label={t(
                "settings.house.projectDataRenewal.form.fields.lastName.label",
              )}
              control={control}
            />
          </div>
          <div className="flex flex-col justify-between gap-4 sm:flex-row">
            <FormInput
              name="email"
              label={t(
                "settings.house.projectDataRenewal.form.fields.email.label",
              )}
              control={control}
            />
            <FormInput
              name="phone"
              label={t(
                "settings.house.projectDataRenewal.form.fields.phone.label",
              )}
              control={control}
            />
          </div>
          <FormCheckbox
            name="updateCustomerData"
            control={control}
            label={t(
              "settings.house.projectDataRenewal.form.fields.updateCustomerData.label",
            )}
          />
          {!isNullOrUndefined(isOwnerValue) && (
            <FormCheckbox
              name="transferringContactsAccepted"
              label={parseHtml(
                t(
                  `settings.house.projectDataRenewal.form.fields.transferringContactsAccepted.${projectData?.projectTypeId ?? projectTypeId}.${
                    isOwnerValue ? "owner" : "payer"
                  }.label`,
                  {
                    link: t(
                      `externalProject.${projectData?.projectTypeId ?? projectTypeId}.rulesLink`,
                    ),
                  },
                ),
              )}
              control={control}
            />
          )}
          {!customerData?.hasAcceptedMarketingRules && (
            <FormAgreementInput
              marketingRulesAcceptedName="marketingRulesAccepted"
              control={control}
            />
          )}
        </form>
      </div>
    </Modal>
  );
};
